<template>
  <div
    ref="bodyRef"
    class="speech-bubble"
    v-bind="clickActionAttrs()"
    @click="onClick"
    @keydown="onKeyDown"
  >
    <svg
      v-if="size.w > 0 && size.h > 0"
      class="speech-bubble-svg"
      :viewBox="`0 0 ${size.w} ${size.h}`"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path :d="pathD" stroke-linejoin="round" />
    </svg>
    <div class="speech-bubble-content">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import {
  composeShadow,
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '../../../composables/useStyleData'
import { useResolveCssSize } from '../../../composables/useResolveCssSize'
import { useClickAction } from '@render/composables/useClickAction'
import { buildSpeechBubblePath, type TailSide } from './speechBubblePath'
import type { SpeechBubbleFields } from './definition'

const RADIUS_DEFAULT = 'var(--radius-sm)'
const TAIL_WIDTH_DEFAULT = '12px'
const TAIL_HEIGHT_DEFAULT = '8px'
const TAIL_OFFSET_DEFAULT = 20
const BG_DEFAULT = 'var(--color-white, #ffffff)'
const BORDER_DEFAULT = 'transparent'

const props = withDefaults(
  defineProps<{
    fields?: SpeechBubbleFields
  }>(),
  { fields: () => ({}) },
)

const emit = defineEmits(['onClick'])

// The bubble is a plain <div>. It already handled Enter/Space, but without a
// role it was announced as inert text and without a tabindex the keyboard
// could never reach it. Both appear only while an onClick processor is bound.
const { hasClickAction, clickActionAttrs } = useClickAction()

const bodyRef = ref<HTMLDivElement | null>(null)
const size = reactive({ w: 0, h: 0 })

const { resolvePx } = useResolveCssSize(bodyRef)

let observer: ResizeObserver | null = null
let measureRaf = 0

function measure() {
  if (!bodyRef.value) return
  size.w = bodyRef.value.offsetWidth
  size.h = bodyRef.value.offsetHeight
}

// Defer the size write out of the ResizeObserver delivery cycle. Writing
// reactive size state synchronously inside the callback can resize this
// subtree again in the same frame, which the browser reports as
// "ResizeObserver loop completed with undelivered notifications".
function scheduleMeasure() {
  if (measureRaf) return
  measureRaf = requestAnimationFrame(() => {
    measureRaf = 0
    measure()
  })
}

onMounted(() => {
  measure()
  if (typeof ResizeObserver !== 'undefined' && bodyRef.value) {
    observer = new ResizeObserver(scheduleMeasure)
    observer.observe(bodyRef.value)
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
  if (measureRaf) cancelAnimationFrame(measureRaf)
})

const tailTipRadiusPx = computed(() => resolvePx(getSizeStyleData(props.fields?.tailTipRadius)))
const strokePxWidth = computed(() => resolvePx(getSizeStyleData(props.fields?.borderWidth)))

const pathD = computed(() => {
  if (size.w <= 0 || size.h <= 0) return ''
  const fields = props.fields
  return buildSpeechBubblePath({
    width: size.w,
    height: size.h,
    borderWidth: strokePxWidth.value,
    radii: {
      tl: resolvePx(getSizeStyleData(fields?.radiusTopLeft) || RADIUS_DEFAULT),
      tr: resolvePx(getSizeStyleData(fields?.radiusTopRight) || RADIUS_DEFAULT),
      br: resolvePx(getSizeStyleData(fields?.radiusBottomRight) || RADIUS_DEFAULT),
      bl: resolvePx(getSizeStyleData(fields?.radiusBottomLeft) || RADIUS_DEFAULT),
    },
    tail: {
      show: fields?.showTail !== false,
      side: (fields?.tailPosition || 'bottom') as TailSide,
      offsetPercent:
        typeof fields?.tailOffsetPercent === 'number'
          ? fields.tailOffsetPercent
          : TAIL_OFFSET_DEFAULT,
      width: resolvePx(getSizeStyleData(fields?.tailWidth) || TAIL_WIDTH_DEFAULT),
      height: resolvePx(getSizeStyleData(fields?.tailHeight) || TAIL_HEIGHT_DEFAULT),
      tipRadius: tailTipRadiusPx.value,
    },
  })
})

const css = computed(() => {
  const fields = props.fields
  const layout = fields?.layout || 'block'
  const isFlex = layout !== 'block'
  const tl = getSizeStyleData(fields?.radiusTopLeft) || RADIUS_DEFAULT
  const tr = getSizeStyleData(fields?.radiusTopRight) || RADIUS_DEFAULT
  const br = getSizeStyleData(fields?.radiusBottomRight) || RADIUS_DEFAULT
  const bl = getSizeStyleData(fields?.radiusBottomLeft) || RADIUS_DEFAULT
  return {
    margin: getSpacingStyleData(fields?.margin) || '0',
    width: getSizeStyleData(fields?.width) || 'auto',
    minWidth: getSizeStyleData(fields?.minWidth) || 'auto',
    maxWidth: getSizeStyleData(fields?.maxWidth) || 'none',
    height: getSizeStyleData(fields?.height) || 'auto',
    minHeight: getSizeStyleData(fields?.minHeight) || 'auto',
    maxHeight: getSizeStyleData(fields?.maxHeight) || 'none',
    borderRadius: `${tl} ${tr} ${br} ${bl}`,
    boxShadow: composeShadow(fields?.dropShadow, fields?.innerShadow),

    fillColor: getColorStyleData(fields?.bgColor) || BG_DEFAULT,
    strokeColor: getColorStyleData(fields?.borderColor) || BORDER_DEFAULT,
    strokeWidth: `${strokePxWidth.value}px`,

    padding: getSpacingStyleData(fields?.padding) || '8px 12px',
    contentDisplay: isFlex ? 'flex' : 'block',
    flexDirection: isFlex ? layout : 'row',
    gap: getSpacingStyleData(fields?.gap) || '0',
    alignItems: fields?.alignItems || 'stretch',
    justifyContent: fields?.justifyContent || 'flex-start',
  }
})

function onClick(event: MouseEvent) {
  emit('onClick', event)
}

function onKeyDown(event: KeyboardEvent) {
  if (!hasClickAction()) return
  if (event.target !== event.currentTarget) return
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  emit('onClick', event)
}
</script>

<style scoped lang="scss">
.speech-bubble {
  position: relative;
  display: inline-block;
  box-sizing: border-box;
  margin: v-bind('css.margin');
  width: v-bind('css.width');
  min-width: v-bind('css.minWidth');
  max-width: v-bind('css.maxWidth');
  height: v-bind('css.height');
  min-height: v-bind('css.minHeight');
  max-height: v-bind('css.maxHeight');
  border-radius: v-bind('css.borderRadius');
  box-shadow: v-bind('css.boxShadow');
}

.speech-bubble-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
  z-index: 0;

  path {
    fill: v-bind('css.fillColor');
    stroke: v-bind('css.strokeColor');
    stroke-width: v-bind('css.strokeWidth');
  }
}

.speech-bubble-content {
  position: relative;
  z-index: 1;
  padding: v-bind('css.padding');
  display: v-bind('css.contentDisplay');
  flex-direction: v-bind('css.flexDirection');
  gap: v-bind('css.gap');
  align-items: v-bind('css.alignItems');
  justify-content: v-bind('css.justifyContent');
}
</style>
